import React, { useMemo } from "react";
import { AiccDefaultScenario, AiccDefaultScenarioPayload } from "types/model/aiccLearningAssets";
import { aiccDefaultScenarioApi } from "services/aiccLearningAssets";
import DefaultScenarioModal from "./DefaultScenarioModal";
import getColumns from "./columnConfig";
import LearningAssetPage from "../LearningAssetPage";
import { useAssetCrud } from "../hooks/useAssetCrud";
import { useLearningAssetVersions } from "../hooks/useLearningAssetVersions";

const FIELD_GUIDE_INTRO =
  "Pre-built conversation scenarios automatically assigned to every new user, in Pimsleur App shows under the " +
  "category “Recommended for you”.";

const FIELD_GUIDE = [
  {
    label: "Scenario Title",
    description: 'Short, user-facing name displayed in the scenario list (2-4 words, e.g., "Ordering at a Cafe")',
  },
  {
    label: "Learner Context Description",
    description:
      "A 1-2 sentence scene-setting text shown to the user before starting (e.g., \"It's morning at a cozy " +
      'neighborhood cafe. The barista greets you warmly.")',
  },
  {
    label: "AI Context Description",
    description:
      "System-level instructions defining the AI's character and the user's practice goal (not shown to user). " +
      'E.g., "You are a friendly barista named Maria. The user wants to practice ordering coffee."',
  },
  {
    label: "Voice Gender",
    description: 'The gender of the AI\'s text-to-speech voice: "male" or "female", chosen to match the character',
  },
];

const getLabel = ({ title }: AiccDefaultScenario | AiccDefaultScenarioPayload) => title;

function DefaultScenarioManagement() {
  const state = useLearningAssetVersions(aiccDefaultScenarioApi.fetchVersions);
  const { isEditable, canDelete, isSaving, runWrite } = state;

  const { isModalVisible, editingItem, openModal, closeModal, handleDelete, handleSubmit } = useAssetCrud<
    AiccDefaultScenario,
    AiccDefaultScenarioPayload
  >({ api: aiccDefaultScenarioApi, runWrite, getLabel });

  const columns = useMemo(() => getColumns({ isEditable, canDelete, onEdit: openModal, onDelete: handleDelete }), [
    isEditable,
    canDelete,
    openModal,
    handleDelete,
  ]);

  return (
    <LearningAssetPage<AiccDefaultScenario>
      title="Default Scenario"
      fieldGuideIntro={FIELD_GUIDE_INTRO}
      fieldGuide={FIELD_GUIDE}
      addText="+ Add Default Scenario"
      columns={columns}
      state={state}
      onAdd={() => openModal()}
    >
      <DefaultScenarioModal
        visible={isModalVisible}
        scenario={editingItem}
        isSaving={isSaving}
        onCancel={closeModal}
        onSubmit={handleSubmit}
      />
    </LearningAssetPage>
  );
}

export default DefaultScenarioManagement;
